/**
 * Cross-platform path utilities for handling file system paths
 * Handles Windows, macOS, and Linux path formats correctly
 */

/**
 * Detect the current operating system
 */
export function detectOS(): 'windows' | 'macos' | 'linux' | 'android' | 'unknown' {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();
  
  if (userAgent.includes('android')) {
    return 'android';
  } else if (userAgent.includes('win') || platform.includes('win')) {
    return 'windows';
  } else if (userAgent.includes('mac') || platform.includes('mac')) {
    return 'macos';
  } else if (userAgent.includes('linux') || platform.includes('linux')) {
    return 'linux';
  }
  
  return 'unknown';
}

/**
 * Get the appropriate path separator for the current OS
 */
export function getPathSeparator(): string {
  const os = detectOS();
  return os === 'windows' ? '\\' : '/';
}

/**
 * Join path segments using the appropriate separator for the current OS
 */
export function joinPath(...segments: string[]): string {
  if (segments.length === 0) return '';
  
  // Filter out empty segments and normalize each one
  const cleanSegments = segments
    .filter(segment => segment && segment.trim() !== '')
    .map(segment => normalizePath(segment.trim()));
  
  const separator = getPathSeparator();
  return cleanSegments.join(separator);
}

/**
 * Normalize a path to use the correct separators for the current OS
 */
export function normalizePath(path: string): string {
  if (!path) return '';
  
  const separator = getPathSeparator();
  const os = detectOS();
  
  if (os === 'windows') {
    // For Windows, convert all forward slashes to backslashes
    return path.replace(/\//g, '\\');
  } else {
    // For Unix-like systems, convert all backslashes to forward slashes
    return path.replace(/\\/g, '/');
  }
}

/**
 * Get platform-specific default paths for common directories
 */
export function getDefaultPaths() {
  const os = detectOS();
  const username = getCurrentUsername();
  
  switch (os) {
    case 'windows':
      return {
        documents: `C:\\Users\\${username}\\Documents`,
        downloads: `C:\\Users\\${username}\\Downloads`,
        desktop: `C:\\Users\\${username}\\Desktop`,
        appData: `C:\\Users\\${username}\\AppData\\Roaming`,
        linkBandDefault: `C:\\Users\\${username}\\Documents\\LINK SDK`
      };
    case 'macos':
      return {
        documents: `/Users/${username}/Documents`,
        downloads: `/Users/${username}/Downloads`,
        desktop: `/Users/${username}/Desktop`,
        appData: `/Users/${username}/Library/Application Support`,
        linkBandDefault: `/Users/${username}/Documents/LINK SDK`
      };
    case 'linux':
      return {
        documents: `/home/${username}/Documents`,
        downloads: `/home/${username}/Downloads`,
        desktop: `/home/${username}/Desktop`,
        appData: `/home/${username}/.config`,
        linkBandDefault: `/home/${username}/Documents/LINK SDK`
      };
    case 'android':
      return {
        documents: `/storage/emulated/0/Documents`,
        downloads: `/storage/emulated/0/Download`,
        desktop: `/storage/emulated/0/Desktop`,
        appData: `/storage/emulated/0/Android/data`,
        linkBandDefault: `/storage/emulated/0/Documents/LINK SDK`
      };
    default:
      return {
        documents: `${username}/Documents`,
        downloads: `${username}/Downloads`,
        desktop: `${username}/Desktop`,
        appData: `${username}/.config`,
        linkBandDefault: `${username}/Documents/LINK SDK`
      };
  }
}

/**
 * Get platform-specific recommended storage paths for user guidance
 */
export function getRecommendedStoragePaths(): string[] {
  const os = detectOS();
  const actualUsername = getCurrentUsername();
  
  switch (os) {
    case 'windows':
      return [
        `C:\\Users\\${actualUsername}\\Documents\\LINK SDK`,
        `C:\\Users\\${actualUsername}\\Downloads\\LINK SDK`,
        `C:\\Users\\${actualUsername}\\Desktop\\LINK SDK`,
        'D:\\LINK SDK' // 별도 드라이브
      ];
    case 'macos':
      return [
        `/Users/${actualUsername}/Documents/LINK SDK`,
        `/Users/${actualUsername}/Downloads/LINK SDK`,
        `/Users/${actualUsername}/Desktop/LINK SDK`,
        '/Volumes/ExternalDrive/LINK SDK' // 외장 드라이브
      ];
    case 'linux':
      return [
        `/home/${actualUsername}/Documents/LINK SDK`,
        `/home/${actualUsername}/Downloads/LINK SDK`,
        `/home/${actualUsername}/Desktop/LINK SDK`,
        `/media/${actualUsername}/storage/LINK SDK` // 외장 저장소
      ];
    case 'android':
      return [
        '/storage/emulated/0/Documents/LINK SDK',
        '/storage/emulated/0/Download/LINK SDK',
        '/sdcard/Documents/LINK SDK',
        '/sdcard/Download/LINK SDK'
      ];
    default:
      return [
        `${actualUsername}/Documents/LINK SDK`,
        `${actualUsername}/Downloads/LINK SDK`
      ];
  }
}

/**
 * Try to detect the current username from various sources
 */
export function getCurrentUsername(): string {
  try {
    // 1. 환경 변수에서 사용자명 시도 (Node.js 환경)
    if (typeof process !== 'undefined' && process.env) {
      const username = process.env.USER || process.env.USERNAME || process.env.USERPROFILE?.split('\\').pop();
      if (username) return username;
    }
    
    // 2. 홈 디렉토리 경로에서 사용자명 추출 시도
    if (typeof window !== 'undefined' && window.navigator) {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      
      // 브라우저 환경에서 추정 가능한 사용자명 가져오기
      try {
        // navigator.userAgentData가 있는 경우 (Chrome 90+)
        if ('userAgentData' in navigator && (navigator as any).userAgentData) {
          const brands = (navigator as any).userAgentData.brands;
          // 여기서는 브랜드 정보로부터 사용자명을 추출할 수 없으므로 다른 방법 사용
        }
        
        // 3. localStorage에서 이전에 저장된 사용자명 가져오기
        const savedUsername = localStorage.getItem('linkband-username');
        if (savedUsername && savedUsername !== '[사용자명]') {
          return savedUsername;
        }
        
        // 4. 웹 환경에서 시스템 기본 사용자명 추정
        const os = detectOS();
        switch (os) {
          case 'windows':
            // Windows에서는 환경변수를 직접 접근할 수 없으므로 기본값 사용
            return getStoredOrDefaultUsername('사용자'); // 한국 Windows 기본값
          case 'macos':
            // macOS에서 현재 사용자명 추출 시도
            // 환경변수 또는 기본값 사용
            const fallbackUsername = 'brian_chae';
            
            // 저장된 사용자명이 있는지 확인
            const storedUsername = localStorage.getItem('linkband-username');
            if (storedUsername && storedUsername !== '[사용자명]' && storedUsername.trim() !== '') {
              return storedUsername;
            }
            
            // 저장된 사용자명이 없으면 기본값 저장하고 반환
            saveUsername(fallbackUsername);
            return fallbackUsername;
          case 'linux':
            return getStoredOrDefaultUsername('user'); // Linux 기본값
          case 'android':
            return 'android'; // Android는 전통적인 사용자명 개념 없음
          default:
            return getStoredOrDefaultUsername('user');
        }
      } catch (error) {
        console.warn('사용자명 추출 중 오류:', error);
      }
    }
    
    // 5. 모든 방법 실패 시 기본값 반환
    return getStoredOrDefaultUsername('user');
  } catch (error) {
    console.warn('사용자명 감지 실패:', error);
    return 'user';
  }
}

/**
 * 저장된 사용자명을 가져오거나 기본값 반환
 */
function getStoredOrDefaultUsername(defaultName: string): string {
  try {
    const stored = localStorage.getItem('linkband-username');
    if (stored && stored !== '[사용자명]' && stored.trim() !== '') {
      return stored;
    }
  } catch (error) {
    console.warn('저장된 사용자명 가져오기 실패:', error);
  }
  return defaultName;
}

/**
 * 파일 시스템 경로에서 사용자명 추출
 */
export function extractUsernameFromPath(path: string): string | null {
  try {
    const os = detectOS();
    
    switch (os) {
      case 'windows':
        // Windows: C:\Users\username\... 형태에서 추출
        const windowsMatch = path.match(/^[A-Za-z]:\\Users\\([^\\]+)\\/);
        if (windowsMatch) {
          return windowsMatch[1];
        }
        break;
      case 'macos':
        // macOS: /Users/username/... 형태에서 추출
        const macMatch = path.match(/^\/Users\/([^\/]+)\//);
        if (macMatch) {
          return macMatch[1];
        }
        break;
      case 'linux':
        // Linux: /home/username/... 형태에서 추출
        const linuxMatch = path.match(/^\/home\/([^\/]+)\//);
        if (linuxMatch) {
          return linuxMatch[1];
        }
        break;
    }
    
    return null;
  } catch (error) {
    console.warn('경로에서 사용자명 추출 실패:', error);
    return null;
  }
}

/**
 * 사용자명을 저장
 */
export function saveUsername(username: string): void {
  try {
    if (username && username !== '[사용자명]' && username.trim() !== '') {
      localStorage.setItem('linkband-username', username.trim());
      // Username saved: ${username}
    }
  } catch (error) {
    console.warn('사용자명 저장 실패:', error);
  }
}

/**
 * Generate a smart default path suggestion based on directory name and OS
 */
export function generateSmartPath(directoryName: string, baseDir: 'documents' | 'downloads' | 'desktop' = 'downloads'): string {
  const os = detectOS();
  const actualUsername = getCurrentUsername();
  const defaultPaths = getDefaultPaths();
  
  const basePath = defaultPaths[baseDir];
  return joinPath(basePath, directoryName);
}

/**
 * Validate if a path looks like a valid absolute path for the current OS
 */
export function isValidAbsolutePath(path: string): boolean {
  const os = detectOS();
  
  switch (os) {
    case 'windows':
      // Windows: C:\path\to\folder or \\server\share\path
      return /^[A-Za-z]:\\/.test(path) || /^\\\\/.test(path);
    case 'macos':
    case 'linux':
      // Unix-like: /path/to/folder
      return path.startsWith('/');
    default:
      // Generic check
      return path.includes('/') || path.includes('\\');
  }
}

/**
 * Convert a path to display format (for UI showing)
 */
export function formatPathForDisplay(path: string): string {
  return normalizePath(path);
}

/**
 * Convert a path to system format (for copying to clipboard)
 */
export function formatPathForSystem(path: string): string {
  if (!path) return '';
  
  const normalized = normalizePath(path);
  
  // Additional cleanup for Windows paths
  const os = detectOS();
  if (os === 'windows') {
    // Ensure Windows paths use backslashes consistently
    return normalized.replace(/\//g, '\\');
  }
  
  return normalized;
}

/**
 * Get localized OS name for display
 */
export function getOSDisplayName(): string {
  const os = detectOS();
  
  switch (os) {
    case 'windows':
      return 'Windows';
    case 'macos':
      return 'macOS';
    case 'linux':
      return 'Linux';
    case 'android':
      return 'Android';
    default:
      return '알 수 없음';
  }
} 